import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import FilteringProps from "../filtering/paymenttransaction.json"
import { setPage, setToolbarActions, toPrettyDate } from "../../../../core/Global"
import FlexRow from "../../../../components/base/FlexRow"
import Icon from "../../../../components/base/Icon"
import Modal from "../../../../components/base/Modal"
import { DataTable, DataTableField } from "../../../../components/data/DataTable"
import TransactionSummary from "../components/TransactionSummary"
import PaymentTransaction, {
    PaymentTransactionKinds,
    PaymentTransactionMethods,
    PaymentTransactionOrigins,
    PaymentTransactionResults
} from "../models/PaymentTransaction"

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

const resultLook = (r?: PaymentTransactionResults) => {
    switch (r) {
        case PaymentTransactionResults.Approved: return { text: "Approved", color: "success", icon: "circle-check" }
        case PaymentTransactionResults.Declined: return { text: "Declined", color: "danger", icon: "circle-xmark" }
        default: return { text: "Failed", color: "warning", icon: "triangle-exclamation" }
    }
}

// "Online Payment" rather than anything with "card" in it: both of these are cards, and what
// tells them apart is that one was tapped on a phone in front of the customer while the other
// went through the payment gateway over the internet.
const methodLook = (m?: PaymentTransactionMethods) =>
    m === PaymentTransactionMethods.TapToPay
        ? { text: "Tap to Pay", icon: "wifi" }
        : { text: "Online Payment", icon: "credit-card" }

const originText = (o?: PaymentTransactionOrigins) => {
    switch (o) {
        case PaymentTransactionOrigins.TechnicianDevice: return "Taken by the technician"
        case PaymentTransactionOrigins.Console: return "Taken in the office"
        default: return "Paid by the customer"
    }
}

/**
 * Every attempt the card gateway was asked to make, from either side of the business — the
 * customer paying their invoice in a browser, and the technician tapping a card on their phone.
 *
 * Read-only on purpose. A transaction is the account of something that already happened, so
 * there is nothing here to add, edit or delete; the row that was written when the card was
 * presented is the row for good.
 */
export default function PaymentTransactionsList() {

    const [selected, selectedSet] = useState<PaymentTransaction>()

    useEffect(() => {
        setPage("Card Transactions", "Reports", "Payments")

        return () => {
            setPage("")
            setToolbarActions(undefined)
        }
    }, [])

    const ResultItem = (data: PaymentTransaction) => {
        const look = resultLook(data.result)
        return (
            <div style={{ minWidth: 180 }}>
                <FlexRow>
                    <Icon icon={look.icon as any} className={`text-${look.color} me-2`} size={12} />
                    <span className={`fw-bold text-${look.color}`}>{look.text}</span>
                    {
                        data.kind === PaymentTransactionKinds.Refund &&
                        <span className="badge badge-light-warning ms-2">Refund</span>
                    }
                </FlexRow>

                {
                    data.result === PaymentTransactionResults.Approved
                        ? data.transactionId &&
                        <div className="fs-8 text-gray-600 mt-1">
                            Ref: <span className="fw-bold text-gray-800">{data.transactionId}</span>
                        </div>
                        : data.responseText &&
                        <div className="fs-8 text-gray-600 mt-1" style={{ maxWidth: 240 }}>
                            {data.responseText}
                        </div>
                }
            </div>
        )
    }

    const AmountItem = (data: PaymentTransaction) => (
        <div>
            <div className={`fw-bolder fs-6 ${data.result === PaymentTransactionResults.Approved
                ? (data.kind === PaymentTransactionKinds.Refund ? "text-warning" : "text-success")
                : "text-gray-500"}`}>
                {data.kind === PaymentTransactionKinds.Refund ? "-" : ""}
                {money.format(data.amount ?? 0)}
            </div>
            {
                (data.tip ?? 0) > 0 &&
                <div className="fs-8 text-gray-600 mt-1">
                    incl. {money.format(data.tip ?? 0)} tip
                </div>
            }
        </div>
    )

    const MethodItem = (data: PaymentTransaction) => {
        const look = methodLook(data.method)
        return (
            <div>
                <FlexRow>
                    <Icon icon={look.icon as any} className="me-2 text-gray-600" size={12} />
                    {look.text}
                </FlexRow>
                {
                    data.cardDetails &&
                    <div className="fs-8 text-gray-600 mt-1">
                        {data.cardType ? data.cardType + " " : ""}{data.cardDetails}
                    </div>
                }
            </div>
        )
    }

    const JobItem = (data: PaymentTransaction) => (
        <div style={{ minWidth: 170 }}>
            {
                data.jobId
                    ? <Link to={`/crm/jobs/details/${data.jobId}`} className="fw-bold">
                        #{data.jobNumber}
                    </Link>
                    : <span className="text-gray-500">—</span>
            }
            {
                data.invoiceNumber ?
                    <div className="fs-8 text-gray-600 mt-1">Invoice #{data.invoiceNumber}</div>
                    : <></>
            }
            {
                data.customerName &&
                <FlexRow className="fs-8 text-gray-600 mt-1">
                    <Icon icon="user" className="me-1" size={10} />
                    {data.customerName}
                </FlexRow>
            }
        </div>
    )

    const TechItem = (data: PaymentTransaction) => (
        <div style={{ minWidth: 150 }}>
            {
                data.tech?.fullName
                    ? <FlexRow>
                        <Icon icon="user-helmet-safety" className="me-2 text-gray-600" size={12} />
                        {data.tech.fullName}
                    </FlexRow>
                    : <span className="text-gray-500">Unassigned</span>
            }
            <div className="fs-8 text-gray-600 mt-1">{originText(data.origin)}</div>
        </div>
    )

    const DateItem = (data: PaymentTransaction) => (
        <div>{toPrettyDate(data.createdAt)}</div>
    )

    const Row = (title: string, value?: any) => (
        value === undefined || value === null || value === "" ? <></> :
            <div className="d-flex border-bottom border-gray-200 py-3">
                <div className="text-gray-600 fs-7" style={{ minWidth: 170 }}>{title}</div>
                <div className="fw-bold fs-7" style={{ wordBreak: "break-word" }}>{value}</div>
            </div>
    )

    return (
        <div className="p-5">

            <TransactionSummary />

            <DataTable
                objectName="Transaction"
                filteringProps={FilteringProps}
                apiRoute="/reports/PaymentTransactions/"
                preventCreate
                noAction
                noMultipleActions
                onRowClick={item => selectedSet(item)}>

                <DataTableField title="Result">
                    <ResultItem />
                </DataTableField>

                <DataTableField title="Amount">
                    <AmountItem />
                </DataTableField>

                <DataTableField title="Method">
                    <MethodItem />
                </DataTableField>

                <DataTableField title="Job">
                    <JobItem />
                </DataTableField>

                <DataTableField title="Technician">
                    <TechItem />
                </DataTableField>

                <DataTableField title="Date">
                    <DateItem />
                </DataTableField>

            </DataTable>

            {
                selected &&
                <Modal id="modal-payment-transaction"
                    open visible noFooter
                    size="Large"
                    title="Transaction"
                    subtitle={resultLook(selected.result).text}
                    onClose={() => selectedSet(undefined)}>

                    <div className="px-2">
                        {Row("Result", resultLook(selected.result).text)}
                        {Row("Type", selected.kind === PaymentTransactionKinds.Refund ? "Refund" : "Payment")}
                        {Row("Method", methodLook(selected.method).text)}
                        {Row("Taken by", originText(selected.origin))}
                        {Row("Date", toPrettyDate(selected.createdAt))}

                        {Row("Amount", money.format(selected.amount ?? 0))}
                        {(selected.tip ?? 0) > 0 ? Row("Work", money.format(selected.workAmount ?? 0)) : <></>}
                        {(selected.tip ?? 0) > 0 ? Row("Tip", money.format(selected.tip ?? 0)) : <></>}

                        {Row("Reference", selected.transactionId)}
                        {Row("Auth code", selected.authCode)}
                        {Row("Gateway code", selected.responseCode)}
                        {Row("Gateway said", selected.responseText)}
                        {Row("Address check", selected.avsResponse)}
                        {Row("Security code check", selected.cvvResponse)}
                        {Row("Card", [selected.cardType, selected.cardDetails].filter(f => f).join(" "))}

                        {Row("Job", selected.jobNumber ? "#" + selected.jobNumber : undefined)}
                        {Row("Invoice", selected.invoiceNumber ? "#" + selected.invoiceNumber : undefined)}
                        {Row("Job type", selected.jobType)}
                        {Row("Source", selected.jobSource)}
                        {Row("Metro", selected.metroName)}
                        {Row("Customer", selected.customerName)}
                        {Row("Phone", selected.customerPhone)}
                        {Row("Address", selected.customerAddress)}
                        {Row("Technician", selected.tech?.fullName)}
                        {Row("Recorded by", selected.creatorUser?.fullName)}

                        {
                            selected.gatewayResponse &&
                            <div className="pt-4">
                                <div className="text-gray-600 fs-7 mb-2">Full gateway response</div>
                                <pre className="bg-light p-4 rounded fs-8"
                                    style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", maxHeight: 220, overflow: "auto" }}>
                                    {selected.gatewayResponse}
                                </pre>
                            </div>
                        }
                    </div>

                </Modal>
            }

        </div>
    )
}
